var loading_spinner_default = (() => {
    const customElementName = "loading-spinner";
    if (customElements.get(customElementName)) return;
    class LoadingSpinner extends HTMLElement {
        constructor() {
            super();
            this.defaultPosition = {
                x: "right",
                y: "bottom"
            };
            this.currentPosition = {
                ...this.defaultPosition
            };
            this.activeSources = new Set;
            this.respectSiblings = true;
            this.baseTransform = "";
            this.siblingStack = {};
            this.offsetStack = {};
            this.offsetPriority = [];
            this.handleShowSpinner = this.handleShowSpinner.bind(this);
            this.handleHideSpinner = this.handleHideSpinner.bind(this);
            this.handlePositionChange = this.handlePositionChange.bind(this);
        }
        connectedCallback() {
            this.setupEventListeners();
            this.applyInitialPosition();
        }
        setupEventListeners() {
            document.addEventListener("spinner:show", this.handleShowSpinner);
            document.addEventListener("spinner:hide", this.handleHideSpinner);
            document.addEventListener("spinner:position", this.handlePositionChange);
            document.addEventListener("global-ui:nudge", this.handlePositionChange);
            document.addEventListener("global-ui:bottom-right-nudge", this.handlePositionChange);
            document.addEventListener("spinner:respect-siblings", e => {
                this.respectSiblings = e.detail.enable !== false;
                this._renderTransform();
            });
        }
        handleShowSpinner(event) {
            const {source: source = "default", position: position, transform: transform} = event.detail || {};
            this.activeSources.add(source);
            if (position) this.updatePosition(position);
            if (transform !== void 0) this._processPriorityUpdate(source, transform);
            this._evaluateVisibility();
            this._renderTransform();
        }
        handleHideSpinner(event) {
            const {source: source = "default", resetPosition: resetPosition = false} = event.detail || {};
            this.activeSources.delete(source);
            if (resetPosition) this._processPriorityUpdate(source, "");
            this._evaluateVisibility();
            this._renderTransform();
        }
        handlePositionChange(event) {
            const {source: source = "default", transform: transform, isSibling: isSibling = false, exclusive: exclusive = false, target: target, position: position, lane: lane} = event.detail || {};
            if (lane && ![ "bottom-right", "bottom", "all" ].includes(lane)) return;
            if (target && target !== "loading-spinner" && target !== "all") return;
            if (position) this.updatePosition(position);
            if (transform !== void 0) if (isSibling) this._processSiblingUpdate(source, transform); else this._processPriorityUpdate(source, transform, exclusive);
            this._renderTransform();
        }
        _processSiblingUpdate(source, transform) {
            if (transform === "" || transform === null) delete this.siblingStack[source]; else this.siblingStack[source] = transform;
        }
        _processPriorityUpdate(source, transform, exclusive = false) {
            if (transform === "" || transform === null) {
                delete this.offsetStack[source];
                const index = this.offsetPriority.indexOf(source);
                if (index > -1) this.offsetPriority.splice(index, 1);
            } else {
                this.offsetStack[source] = {
                    transform: transform,
                    exclusive: exclusive
                };
                const index = this.offsetPriority.indexOf(source);
                if (index > -1) this.offsetPriority.splice(index, 1);
                this.offsetPriority.push(source);
            }
        }
        _renderTransform() {
            const base = this.baseTransform || "";
            const sibling = this.respectSiblings ? Object.values(this.siblingStack).filter(val => val && val !== "none").join(" ") : "";
            const prioritySource = this.offsetPriority[this.offsetPriority.length - 1];
            const priorityEntry = prioritySource ? this.offsetStack[prioritySource] : null;
            const rawPriority = priorityEntry?.transform || "";
            const dynamic = rawPriority === "RESET" || rawPriority === "none" ? "" : rawPriority;
            const finalValue = (dynamic && priorityEntry?.exclusive ? [ base, dynamic ] : [ base, sibling, dynamic ]).filter(Boolean).join(" ").trim();
            this.style.transform = finalValue || "none";
            this.style.transition = "transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)";
        }
        _evaluateVisibility() {
            const shouldBeVisible = this.activeSources.size > 0;
            this.classList.toggle("hidden", !shouldBeVisible);
            this.classList.toggle("block", shouldBeVisible);
        }
        updatePosition(position) {
            const {x: x, y: y} = position;
            this.classList.remove("left-0", "right-0", "top-0", "bottom-0", "left-1/2", "top-1/2", "-translate-x-1/2", "-translate-y-1/2", "ml-offset", "mr-offset", "mt-offset", "mb-offset");
            let tx = "0", ty = "0";
            if (x === "center") {
                this.classList.add("left-1/2");
                tx = "-50%";
            } else if (x === "left") this.classList.add("left-0", "ml-offset"); else this.classList.add("right-0", "mr-offset");
            if (y === "center") {
                this.classList.add("top-1/2");
                ty = "-50%";
            } else if (y === "top") this.classList.add("top-0", "mt-offset"); else this.classList.add("bottom-0", "mb-offset");
            this.baseTransform = tx !== "0" || ty !== "0" ? `translate(${tx}, ${ty})` : "";
            this.currentPosition = {
                x: x,
                y: y
            };
        }
        applyInitialPosition() {
            this.updatePosition(this.defaultPosition);
        }
        removeEventListeners() {
            document.removeEventListener("spinner:show", this.handleShowSpinner);
            document.removeEventListener("spinner:hide", this.handleHideSpinner);
            document.removeEventListener("spinner:position", this.handlePositionChange);
            document.removeEventListener("global-ui:nudge", this.handlePositionChange);
            document.removeEventListener("global-ui:bottom-right-nudge", this.handlePositionChange);
        }
    }
    customElements.define(customElementName, LoadingSpinner);
    return {
        show: (source = "default", opts = {}) => document.dispatchEvent(new CustomEvent("spinner:show", {
            detail: {
                source: source,
                ...opts
            }
        })),
        hide: (source = "default", opts = {}) => document.dispatchEvent(new CustomEvent("spinner:hide", {
            detail: {
                source: source,
                ...opts
            }
        })),
        disconnect: () => document.dispatchEvent(new CustomEvent("spinner:respect-siblings", {
            detail: {
                enable: false
            }
        })),
        reconnect: () => document.dispatchEvent(new CustomEvent("spinner:respect-siblings", {
            detail: {
                enable: true
            }
        }))
    };
})();